import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { maintenancePlanService, machineService } from '../services/api';

function formatDateTime(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  return d.toLocaleString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toDatetimeLocal(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function MaintenancePlanPage() {
  const [items, setItems] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    machine: '',
    start_at: '',
    end_at: '',
    comment: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [maintenanceRes, machinesRes] = await Promise.all([
        maintenancePlanService.getAll(),
        machineService.getAllForSelect(),
      ]);
      setItems(maintenanceRes.data.results || maintenanceRes.data);
      setMachines(machinesRes.data.results || machinesRes.data);
    } catch (error) {
      console.error('Помилка завантаження:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (item = null) => {
    if (item) {
      setEditing(item.id);
      setFormData({
        machine: item.machine,
        start_at: toDatetimeLocal(item.start_at),
        end_at: toDatetimeLocal(item.end_at),
        comment: item.comment || '',
      });
    } else {
      setEditing(null);
      const now = new Date();
      const end = new Date(now.getTime() + 60 * 60 * 1000);
      setFormData({
        machine: '',
        start_at: toDatetimeLocal(now.toISOString()),
        end_at: toDatetimeLocal(end.toISOString()),
        comment: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
  };

  const handleSubmit = async () => {
    if (!formData.machine || !formData.start_at || !formData.end_at) {
      alert('Заповніть станок, дату/час з та дату/час по.');
      return;
    }
    const start = formData.start_at.length === 16 ? `${formData.start_at}:00` : formData.start_at;
    const end = formData.end_at.length === 16 ? `${formData.end_at}:00` : formData.end_at;
    try {
      const payload = {
        machine: Number(formData.machine),
        start_at: start,
        end_at: end,
        comment: formData.comment || '',
      };
      if (editing) {
        await maintenancePlanService.update(editing, payload);
      } else {
        await maintenancePlanService.create(payload);
      }
      handleClose();
      loadData();
    } catch (error) {
      console.error('Помилка збереження:', error);
      const msg =
        error.response?.data?.detail ||
        (typeof error.response?.data === 'object' && error.response?.data !== null
          ? Object.entries(error.response.data)
              .map(([k, v]) => (Array.isArray(v) ? v.join(' ') : v))
              .join(' ')
          : null) ||
        error.message ||
        'Помилка збереження даних';
      alert(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити цей запис з плану обслуговування?')) return;
    try {
      await maintenancePlanService.delete(id);
      loadData();
    } catch (error) {
      console.error('Помилка видалення:', error);
      alert('Помилка видалення');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">План обслуговування</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Додати запис
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Станок</TableCell>
              <TableCell>Дата і час з</TableCell>
              <TableCell>Дата і час по</TableCell>
              <TableCell>Коментар</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Немає записів. Додайте запис про технічне обслуговування.
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    {item.machine_name || item.machine_identifier || `#${item.machine}`}
                  </TableCell>
                  <TableCell>{formatDateTime(item.start_at)}</TableCell>
                  <TableCell>{formatDateTime(item.end_at)}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>{item.comment || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleOpen(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? 'Редагувати запис' : 'Додати запис про обслуговування'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              select
              label="Станок"
              value={formData.machine}
              onChange={(e) => setFormData({ ...formData, machine: e.target.value })}
              fullWidth
              required
            >
              <MenuItem value="">— обрати —</MenuItem>
              {machines.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.identifier} — {m.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Дата і час з"
              type="datetime-local"
              value={formData.start_at}
              onChange={(e) => setFormData({ ...formData, start_at: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
            <TextField
              label="Дата і час по"
              type="datetime-local"
              value={formData.end_at}
              onChange={(e) => setFormData({ ...formData, end_at: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
            <TextField
              label="Коментар"
              multiline
              rows={3}
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MaintenancePlanPage;
